import { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories, faqs } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const category = useMemo(
    () => (article ? categories.find((c) => c.slug === article.category) : undefined),
    [article],
  );

  if (!slug || !article) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const faqForSchema = faqs.slice(0, 6);

  const schemaOrgData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '经典文学研究院',
      url: 'https://epyuanchang.cn/',
    },
    datePublished: article.date,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://epyuanchang.cn/article/${article.slug}`,
    },
    articleSection: category?.name || '',
    wordCount: article.content.length,
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://epyuanchang.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '',
        item: `https://epyuanchang.cn/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqForSchema.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category?.name || '', path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-3 py-1 bg-primary-50 text-primary-500 rounded-full font-medium whitespace-nowrap hover:bg-primary-100 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <span className="text-sm text-foreground-500">{article.date}</span>
              <span className="text-sm text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              {article.title}
            </h1>
            <div className="flex items-center gap-2 mt-4 text-sm text-foreground-500">
              <i className="ri-user-line"></i>
              <span>{article.author}</span>
            </div>
          </header>

          <div className="article-content max-w-none mb-16 text-foreground-800 leading-relaxed">
            {(() => {
              const parts = article.content.split(/<h2>(.*?)<\/h2>/);
              const elements: JSX.Element[] = [];
              for (let i = 0; i < parts.length; i++) {
                if (i % 2 === 1 && parts[i]) {
                  elements.push(
                    <h2 key={`h-${i}`} className="text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                      {parts[i]}
                    </h2>
                  );
                } else if (parts[i] && parts[i].trim()) {
                  elements.push(
                    <div key={`c-${i}`} className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4" dangerouslySetInnerHTML={{ __html: parts[i] }} />
                  );
                }
              }
              return elements;
            })()}
          </div>

          {relatedArticles.length > 0 && (
            <section className="border-t border-background-200/70 pt-10 pb-16">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="bg-background-100 rounded-lg p-4 md:p-5 hover:bg-background-200/60 transition-colors duration-200 cursor-pointer"
                  >
                    <h4 className="text-sm md:text-base font-medium text-foreground-950 mb-2 leading-snug line-clamp-2 hover:text-primary-500 transition-colors">
                      {related.title}
                    </h4>
                    <p className="text-xs text-foreground-500 line-clamp-2">
                      {related.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrgData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}